import { useState } from 'react';
import { DashboardLayout } from '@/backoffice/components/layout';
import { Card } from '@/backoffice/components/ui';
import {
  HelpCircle,
  ChevronDown,
  Phone,
  Mail,
  MessageCircle,
  Package,
  Wallet,
  MapPin,
} from 'lucide-react';
import { cn } from '@/utils/cn';

interface FaqItem {
  question: string;
  answer: string;
}

const FAQ_ITEMS: FaqItem[] = [
  {
    question: 'Comment créer une nouvelle commande ?',
    answer:
      'Depuis le tableau de bord, cliquez sur « Nouvelle commande » ou rendez-vous dans Commandes. Renseignez les adresses de ramassage et de livraison, choisissez le type de livraison puis confirmez.',
  },
  {
    question: 'Comment suivre ma livraison en temps réel ?',
    answer:
      'Une fois la commande prise en charge par un livreur, accédez à « Suivi en direct » pour voir sa position sur la carte et l\'état de la livraison.',
  },
  {
    question: 'Quand dois-je payer ma commande ?',
    answer:
      'Vous pouvez payer immédiatement à la création ou choisir « Payer à la livraison ». Dans ce cas, confirmez et payez depuis la page de la commande une fois le colis livré.',
  },
  {
    question: 'Comment recharger mon portefeuille ?',
    answer:
      'Allez dans Portefeuille, cliquez sur « Déposer » et suivez les instructions Mobile Money (Orange ou MTN). Le solde est mis à jour après confirmation du paiement.',
  },
  {
    question: 'Puis-je modifier ou annuler une commande ?',
    answer:
      'Tant que la commande est en attente (non prise en charge), contactez le support. Une fois en cours de livraison, l\'annulation n\'est plus possible.',
  },
];

function FaqAccordion({ item }: { item: FaqItem }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="border-b border-gray-100 last:border-0">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-4 py-4 text-left"
      >
        <span className="font-medium text-gray-900">{item.question}</span>
        <ChevronDown
          className={cn('w-5 h-5 text-gray-400 shrink-0 transition-transform', open && 'rotate-180')}
        />
      </button>
      {open && (
        <p className="pb-4 text-sm text-gray-600 leading-relaxed">{item.answer}</p>
      )}
    </div>
  );
}

export function HelpPage() {
  return (
    <DashboardLayout
      userType="client"
      title="Aide"
      breadcrumbs={[
        { label: 'Tableau de bord', path: '/dashboard/client' },
        { label: 'Aide' },
      ]}
    >
      <div className="max-w-3xl mx-auto space-y-6">
        <Card>
          <div className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <HelpCircle className="w-5 h-5 text-teal-600" />
              <h3 className="text-lg font-semibold text-gray-900">Questions fréquentes</h3>
            </div>
            {FAQ_ITEMS.map((item) => (
              <FaqAccordion key={item.question} item={item} />
            ))}
          </div>
        </Card>

        <Card>
          <div className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Contacter le support</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <a
                href="tel:+224620000000"
                className="flex flex-col items-center gap-2 p-4 rounded-xl border border-gray-200 hover:border-teal-300 hover:bg-teal-50/50 transition-colors text-center"
              >
                <Phone className="w-6 h-6 text-teal-600" />
                <span className="font-medium text-gray-900">Téléphone</span>
                <span className="text-sm text-gray-500">+224 620 00 00 00</span>
              </a>
              <a
                href="mailto:support@onapila.com"
                className="flex flex-col items-center gap-2 p-4 rounded-xl border border-gray-200 hover:border-teal-300 hover:bg-teal-50/50 transition-colors text-center"
              >
                <Mail className="w-6 h-6 text-teal-600" />
                <span className="font-medium text-gray-900">E-mail</span>
                <span className="text-sm text-gray-500">support@onapila.com</span>
              </a>
              <a
                href="https://wa.me/224620000000"
                target="_blank"
                rel="noopener noreferrer"
                className="flex flex-col items-center gap-2 p-4 rounded-xl border border-gray-200 hover:border-teal-300 hover:bg-teal-50/50 transition-colors text-center"
              >
                <MessageCircle className="w-6 h-6 text-teal-600" />
                <span className="font-medium text-gray-900">WhatsApp</span>
                <span className="text-sm text-gray-500">Chat en direct</span>
              </a>
            </div>
          </div>
        </Card>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card>
            <div className="p-5 flex items-start gap-3">
              <Package className="w-5 h-5 text-teal-600 shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-gray-900">Commandes</p>
                <p className="text-sm text-gray-500 mt-1">Création, suivi et paiement</p>
              </div>
            </div>
          </Card>
          <Card>
            <div className="p-5 flex items-start gap-3">
              <Wallet className="w-5 h-5 text-teal-600 shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-gray-900">Portefeuille</p>
                <p className="text-sm text-gray-500 mt-1">Dépôts et retraits Mobile Money</p>
              </div>
            </div>
          </Card>
          <Card>
            <div className="p-5 flex items-start gap-3">
              <MapPin className="w-5 h-5 text-teal-600 shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-gray-900">Adresses</p>
                <p className="text-sm text-gray-500 mt-1">Gérer vos lieux favoris</p>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
}
